import { Component, EventEmitter, Output, signal, computed, inject, model, input, output, ChangeDetectionStrategy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ModalService } from 'src/app/services/modal.service';
import { CustomerService } from '../customer.service';
import { AdminResourceListUpdate, Employee, SystemAdminInfo } from '../customer.model';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { AdminDepartment } from '../../resource-access/resource-access.models';

import { SearchInputComponent } from 'src/app/common/general/search-input/search-input.component';
import { ErrorService } from '../../../common/errors/error.service';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
    selector: 'app-customer-admin-access',
    templateUrl: './customer-admin-access.component.html',
    styleUrl: './customer-admin-access.component.scss',
    imports: [SearchInputComponent],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: true
})
export class CustomerAdminAccessComponent {
  // ===== DEPENDENCY INJECTION =====
  private readonly customerService = inject(CustomerService);
  public readonly modalService = inject(ModalService);
  public readonly activeModal = inject(NgbActiveModal);
  private readonly router = inject(Router);
  private readonly toastr = inject(ToastrService);
  private readonly errorService = inject(ErrorService);

  // ===== INPUT/OUTPUT PROPERTIES =====
  kundeid = -1;

  // ===== DATA SIGNALS & STATE =====
  readonly isEmployeesLoading = signal(false);
  readonly employees = signal<Employee[]>([]);
  readonly selectedAdmins = signal<Employee[]>([]);
  readonly systemAdmins = signal<Employee[]>([]);
  readonly searchText = model('');
  readonly waitingNetworkResponse = signal(false);
  readonly systemAdminInfo = signal<SystemAdminInfo[]>([]);
  readonly filteredEmployees = signal<Employee[]>([]);

  // Computed signals for business logic
  readonly availableEmployees = computed(() => {
    const allEmployees = this.employees();
    const selected = this.selectedAdmins();
    const system = this.systemAdmins();
    
    return allEmployees.filter(emp =>
      !selected.some(admin => admin.id === emp.id) &&
      !system.some(sysAdmin => sysAdmin.id === emp.id)
    );
  });

  // Initialize filtered employees with all available employees
  private initializeFilteredEmployees() {
    this.filteredEmployees.set(this.availableEmployees());
  }

  onFilteredEmployeesChange(filtered: Employee[]) {
    this.filteredEmployees.set(filtered);
  }

  //#region Business Logic
  async ngOnInit(): Promise<void> {
    const kundeid = this.kundeid;
    if (!Number.isSafeInteger(kundeid)) {
      this.errorService.notify('KundeId [' + kundeid + '] er ikke et gyldigt kundeId');
      this.router.navigate(['/home']);
      return;
    }

    // Initialize UI
    this.initializeUI();

    this.isEmployeesLoading.set(true);
    
    try {
      const [employees, evAdminsResponse, systemAdminInfo] = await Promise.all([
        this.customerService.getEmployees(),
        this.customerService.getEvAdmins(kundeid),
        this.customerService.getSystemAdminInfo(kundeid)
      ]);

      this.employees.set(employees);
      
      const selectedAdminGuids = evAdminsResponse.selectedAdmins;
      const selectedAdmins = employees
        .filter(x => selectedAdminGuids.includes(x.id))
        .sort((a, b) => a.name.localeCompare(b.name));
      this.selectedAdmins.set(selectedAdmins);
      
      const systemAdminGuids = evAdminsResponse.systemAdmins;
      const systemAdmins = employees
        .filter(x => systemAdminGuids.includes(x.id))
        .sort((a, b) => a.name.localeCompare(b.name));
      this.systemAdmins.set(systemAdmins);
      
      this.systemAdminInfo.set(systemAdminInfo);
      this.initializeFilteredEmployees();
    } catch (error) {
      console.error(error);
    } finally {
      this.isEmployeesLoading.set(false);
    }
  }

  toggleSelection(resource: Employee) {
    const currentSelected = this.selectedAdmins();
    const isSelected = currentSelected.some(x => x.id === resource.id);
    
    if (isSelected) {
      const filtered = currentSelected
        .filter(x => x.id !== resource.id)
        .sort((a, b) => a.name.localeCompare(b.name));
      this.selectedAdmins.set(filtered);
    } else {
      const updated = [...currentSelected, resource]
        .sort((a, b) => a.name.localeCompare(b.name));
      this.selectedAdmins.set(updated);
    }
  }

  async save() {
    const request: AdminResourceListUpdate = {
      customerId: this.kundeid,
      adminResourceGuids: this.selectedAdmins().map(x => x.id)
    };
  
    try {
      this.waitingNetworkResponse.set(true);
      await this.customerService.updateCustomerAdminResources(request);
      this.activeModal.close();
      this.toastr.success('Admin ressourcer adgang opdateret.', 'Success');
    } catch (e: HttpErrorResponse | any) {
      console.error(e);
    } finally {
      this.waitingNetworkResponse.set(false);
    }
  }

  GetAdminDepartmentTitle(department: AdminDepartment) {
    return [department.departmentName, "", ...department.members].join('\n');
  }

  // UI Helper methods for templates
  isEmployeeSelected(employeeId: string): boolean {
    return this.selectedAdmins().some(admin => admin.id === employeeId);
  }
  //#endregion

  //#region Frontend/DOM Manipulation Logic

  // UI-specific signals
  readonly selectedAdminsTableHeight = signal(0); // Will be calculated on init
  private readonly OTHER_ELEMENTS_HEIGHT = 115; // Height of other UI elements in px

  // UI computed signals
  readonly availableEmployeesTableHeight = computed(() => {
    const totalAvailableHeight = this.getTotalAvailableTableHeight();
    const selectedHeight = this.selectedAdminsTableHeight();
    const remainingHeight = totalAvailableHeight - selectedHeight;
    return Math.max(200, remainingHeight); // Minimum 200px for bottom table
  });

  // UI initialization
  private initializeUI(): void {
    const totalHeight = this.getTotalAvailableTableHeight();
    this.selectedAdminsTableHeight.set(Math.floor(totalHeight * 0.382));
  }

  private getTotalAvailableTableHeight(): number {
    // Modal body is 80vh
    const modalBodyHeight = window.innerHeight * 0.8;     
    
    return modalBodyHeight - this.OTHER_ELEMENTS_HEIGHT;
  }

  // DOM manipulation for resize functionality
  onResizeStart(event: MouseEvent): void {
    event.preventDefault();
    const startY = event.clientY;
    const startHeight = this.selectedAdminsTableHeight();
    const totalHeight = this.getTotalAvailableTableHeight();

    const onMouseMove = (e: MouseEvent) => {
      const deltaY = e.clientY - startY;
      const newHeight = startHeight + deltaY;
      
      // Constrain the height so both tables have minimum 200px
      const minTopHeight = 200;
      const minBottomHeight = 200;
      const maxTopHeight = totalHeight - minBottomHeight;
      
      const constrainedHeight = Math.max(minTopHeight, Math.min(maxTopHeight, newHeight));
      this.selectedAdminsTableHeight.set(constrainedHeight);
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }
  //#endregion
}
